import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Card, Typography, Space, Button, Tag } from "antd";
import { ThunderboltOutlined, GiftOutlined, RightOutlined } from "@ant-design/icons";
import { VoucherStatus } from "../helpers/api";
import { isLegacyWebtransferBrand } from "../utils/miniAppBrand";

const { Text, Title } = Typography;

interface VoucherBannerProps {
  onOpenVoucher: () => void;
  voucher: VoucherStatus | null;
}

export const VoucherBanner: FC<VoucherBannerProps> = ({ onOpenVoucher, voucher }) => {
  const { t } = useTranslation();

  // Баннер показываем только пока ваучер реально доступен: истёкший,
  // выключенный или уже использованный не рекламируем.
  if (!voucher || !voucher.available) {
    return null;
  }

  return (
    <Card
      style={{
        background: "linear-gradient(135deg, #096dd9 0%, #1890ff 50%, #722ed1 100%)",
        borderRadius: "16px",
        border: "none",
        color: "#ffffff",
        boxShadow: "0 8px 24px rgba(24, 144, 255, 0.25)",
        overflow: "hidden",
        position: "relative",
        marginBottom: "16px",
        cursor: "pointer",
      }}
      bodyStyle={{ padding: "18px 16px" }}
      onClick={onOpenVoucher}
    >
      {/* Background Decorative Blur Circle */}
      <div
        style={{
          position: "absolute",
          top: "-30px",
          right: "-30px",
          width: "120px",
          height: "120px",
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.12)",
          pointerEvents: "none",
        }}
      />

      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Tag
            color="gold"
            style={{
              borderRadius: "12px",
              fontWeight: 700,
              padding: "2px 10px",
              border: "none",
              fontSize: "12px",
              letterSpacing: "0.5px",
            }}
          >
            {isLegacyWebtransferBrand ? "💎 " : ""}{t("PERSONAL 2X VOUCHER")}
          </Tag>
          <Tag
            style={{
              background: "rgba(255,255,255,0.2)",
              color: "#fff",
              borderRadius: "12px",
              border: "none",
              fontSize: "11px",
            }}
          >
            {isLegacyWebtransferBrand ? "⏱ " : ""}{t("Until end of month")}
          </Tag>
        </div>

        <div>
          <Title level={4} style={{ color: "#fff", margin: 0, fontWeight: 700, fontSize: "17px" }}>
            {t("Capital Doubling Voucher")}
          </Title>
          <Text style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "13px", lineHeight: "1.3" }}>
            {isLegacyWebtransferBrand
              ? t("Double the amount of your next top-up in GRAM or USDT!")
              : t("Double the amount of your next deposit — any currency, any network.")}
          </Text>
        </div>

        <div
          style={{
            background: "rgba(255, 255, 255, 0.12)",
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            padding: "10px 12px",
            fontSize: "12px",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <ThunderboltOutlined style={{ color: "#ffd666" }} />
            <span>
              {isLegacyWebtransferBrand
                ? t("Example: top up {{amount}} GRAM → get {{doubled}} GRAM working", {
                    amount: 100,
                    doubled: 200,
                  })
                : t("Example: top up {{amount}} → get {{doubled}} on your working balance", {
                    amount: 100,
                    doubled: 200,
                  })}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <GiftOutlined style={{ color: "#73d13d" }} />
            <span>{t("Withdrawal: 10% per month including profit")}</span>
          </div>
        </div>

        <Button
          type="primary"
          block
          size="large"
          icon={<GiftOutlined />}
          style={{
            background: "#ffffff",
            color: "#096dd9",
            borderColor: "#ffffff",
            fontWeight: 700,
            borderRadius: "12px",
            height: "44px",
            fontSize: "14px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {isLegacyWebtransferBrand ? "🎫 " : ""}{t("Activate Voucher")} <RightOutlined style={{ fontSize: "12px", marginLeft: 4 }} />
        </Button>
      </Space>
    </Card>
  );
};

export default VoucherBanner;
